import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { AsyncPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { FacettenModalButtonComponent } from '../facetten/facetten-modal-button/facetten-modal-button.component';
import { CheckboxListeComponent } from './checkbox-liste/checkbox-liste.component';
import { FilterSelectItem } from './checkbox-liste/filterSelectItem';

@Component({
  selector: 'now-wbsuche-lib-checkbox-facette',
  templateUrl: './checkbox-facette.component.html',
  styleUrls: ['./checkbox-facette.component.scss'],
  imports: [FacettenModalButtonComponent, FormsModule, CheckboxListeComponent, NgClass, AsyncPipe]
})
export class CheckboxFacetteComponent {
  @Input({ required: true })
  public komponentenId!: string;

  @Input()
  selectItems: FilterSelectItem[] = [];

  @Input()
  searchable = false;

  @Input()
  modalId?: string;

  @Input()
  maxHeight: number | 'none' = 'none';

  @Input()
  forceTextWrap: boolean = false;

  @Input({ required: true }) label!: string;

  @Input({ required: true })
  set facetteLoading(loading: boolean) {
    this.facetteLoading$.next(loading);
  }

  @Input()
  pinSelectedAtTop = false;

  @Output()
  selected = new EventEmitter<FilterSelectItem[]>();

  @Input()
  set countOfSelectedItems(countOfSelectedItems: number) {
    this._countOfSelectedItems.next(countOfSelectedItems);
  }

  @ViewChild('checkboxList', { read: ElementRef }) checkboxList!: ElementRef;

  private readonly _countOfSelectedItems = new BehaviorSubject(0);

  private readonly facetteLoading$ = new BehaviorSubject(false);

  public get countOfSelectedItems() {
    return this._countOfSelectedItems.getValue();
  }

  public filterSuchwort = '';
  public selectedItemVisible$ = new BehaviorSubject(false);
  public selectedItemBtnVisible$ = combineLatest([
    this._countOfSelectedItems,
    this.selectedItemVisible$,
    this.facetteLoading$
  ]).pipe(
    map(([count, selectedItemVisible, loading]) => {
      return count > 0 && !selectedItemVisible && !loading;
    })
  );

  /**
   * Liefere nur die Optionen ggf. sortiert zurück
   */
  public get sortedSelectItems() {
    if (this.pinSelectedAtTop) {
      return (this.selectItems ?? [])
        .sort((a, b) => this.sortableString(a.label).localeCompare(this.sortableString(b.label)))
        .sort((a, b) => (b.selected ? 1 : -1) - (a.selected ? 1 : -1));
    }
    return this.selectItems ?? [];
  }

  /**
   * Liefere nur die Optionen zurück,
   * die dem Suchwort entsprechen (case-IN-sensitive),
   * oder selektiert sind
   * für Filterung / searchable
   */
  public get filteredSelectItems() {
    return this.sortedSelectItems.filter(
      (item) =>
        String(item?.label ?? '')
          .toLowerCase()
          .includes(this.filterSuchwort.toLowerCase()) ||
        (this.pinSelectedAtTop && item.selected)
    );
  }

  private sortableString(string: string | SafeHtml): string {
    return String(string).replace(/\W+/g, '');
  }

  setFilter(filter: string) {
    this.filterSuchwort = filter;
  }

  clearFilter() {
    this.filterSuchwort = '';
  }

  public onItemSelected(item: FilterSelectItem): void {
    this.selectItems = this.selectItems.map((selectItem) => {
      if (selectItem.key === item.key) {
        return {
          ...selectItem,
          selected: !selectItem.selected
        };
      }
      return selectItem;
    });
    const selectedItems = this.selectItems.filter(({ selected }) => selected === true);
    this.selected.emit(selectedItems);
  }

  public onSelectedItemVisible(visible: boolean) {
    this.selectedItemVisible$.next(visible);
  }

  public scrollToFirstItem() {
    this.checkboxList.nativeElement
      .querySelector('[role="listbox"]')
      .scrollTo({ top: 0, behavior: 'smooth' });
  }
}
